import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { SERVER_URL } from './config';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const logoutTimerRef = useRef(null);
  const [initialized, setInitialized] = useState(false);

  // Logout function
  const clearLogoutTimer = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  const logout = useCallback(() => {
    clearLogoutTimer();
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Decode token to extract user details and set logout timer
  const decodeToken = useCallback((token) => {
    try {
      // Decode JWT payload (base64url safe)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
  const exp = decoded.exp * 1000; // Convert to milliseconds

  // Set timeout for logout exactly at expiration
  const timeout = exp - Date.now();
  if (timeout > 0) {
        clearLogoutTimer();
        logoutTimerRef.current = setTimeout(() => {
          logout();
        }, timeout);
      } else {
        logout();
      }

      setUser({ id: decoded.id, username: decoded.sub });
    } catch (error) {
      console.error('Failed to decode token:', error);
      logout();
    }
  }, [logout]);

  // Check for stored token on app load
  useEffect(() => {
    let cancelled = false;

    const validateStoredToken = async (storedToken) => {
      try {
        await axios.get(`${SERVER_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        if (!cancelled) {
          setToken(storedToken);
          setIsAuthenticated(true);
          decodeToken(storedToken);
        }
      } catch (error) {
        if (!cancelled) {
          logout();
        }
      } finally {
        if (!cancelled) {
          setInitialized(true);
        }
      }
    };

    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      validateStoredToken(storedToken);
    } else {
      setInitialized(true);
    }

    // Activity listeners should always use the latest token from storage
    const resetTimer = () => {
      const currentToken = localStorage.getItem('authToken');
      if (currentToken) {
        decodeToken(currentToken);
      }
    };

  window.addEventListener('mousemove', resetTimer);
  window.addEventListener('keypress', resetTimer);

  return () => {
      cancelled = true;
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keypress', resetTimer);
      clearLogoutTimer();
    };
  }, [decodeToken, logout]);

  // Login function
  const login = (receivedToken) => {
    localStorage.setItem('authToken', receivedToken);
    setToken(receivedToken);
    setIsAuthenticated(true);
    decodeToken(receivedToken);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        login,
        logout,
  initialized,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
